import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Inbox, Bug, Lightbulb, Loader2, CheckCircle2, RotateCcw, Trash2, X, Reply, Send, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/newsUtils";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "nuevo", label: "Nuevos", field: "status" },
  { key: "resuelto", label: "Resueltos", field: "status" },
  { key: "bug", label: "Bugs", field: "category" },
  { key: "sugerencia", label: "Sugerencias", field: "category" },
];

export default function AdminFeedback() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [preview, setPreview] = useState(null);
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      const f = FILTERS.find((x) => x.key === filter);
      if (f?.field === "status") params.status = filter;
      if (f?.field === "category") params.category = filter;
      const { data } = await axios.get(`${API}/admin/feedback`, { params });
      setItems(data.items);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar la bandeja");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/feedback/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este comentario? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/admin/feedback/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Comentario eliminado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const openReply = (id) => {
    setReplyingId(id);
    setReplyText("");
  };

  const sendReply = async (id) => {
    const text = replyText.trim();
    if (text.length < 2) {
      toast.error("Escribe una respuesta antes de enviar.");
      return;
    }
    setReplySending(true);
    try {
      await axios.post(`${API}/admin/feedback/${id}/reply`, { message: text });
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, status: "resuelto", reply_message: text } : it))
      );
      toast.success("Respuesta enviada al usuario por email.");
      setReplyingId(null);
      setReplyText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar la respuesta");
    } finally {
      setReplySending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-feedback">
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary flex items-center justify-center">
              <Inbox className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-heading text-lg font-black tracking-tight">BANDEJA DE FEEDBACK</h1>
          </div>
          <Button
            data-testid="admin-back-button"
            variant="outline"
            onClick={() => navigate("/admin")}
            className="rounded-none border-border h-10 w-10 px-0 sm:w-auto sm:px-4 gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Volver</span>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex flex-wrap gap-2" data-testid="feedback-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`feedback-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-4 h-9 text-xs font-semibold uppercase tracking-wide border transition-colors ${
                filter === f.key
                  ? "bg-primary text-white border-primary"
                  : "bg-transparent text-foreground border-border hover:border-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="feedback-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-border py-20 text-center text-muted-foreground" data-testid="feedback-empty">
            No hay comentarios en esta vista.
          </div>
        ) : (
          <div className="space-y-4" data-testid="feedback-list">
            {items.map((it) => {
              const isBug = it.category === "bug";
              const resolved = it.status === "resuelto";
              const Icon = isBug ? Bug : Lightbulb;
              return (
                <div
                  key={it.id}
                  data-testid="feedback-item"
                  className={`border p-5 flex flex-col sm:flex-row gap-4 ${resolved ? "border-border bg-muted/30" : "border-foreground"}`}
                >
                  {it.image && (
                    <button
                      data-testid="feedback-thumb"
                      onClick={() => setPreview(it.image)}
                      className="shrink-0 w-full sm:w-40 h-28 border border-border overflow-hidden group"
                    >
                      <img src={it.image} alt="Captura" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 ${isBug ? "bg-red-50 text-red-700" : "bg-primary/10 text-primary"}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {isBug ? "Bug" : "Sugerencia"}
                      </span>
                      {resolved && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 bg-green-50 text-green-700">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Resuelto
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto whitespace-nowrap">{fmtDate(it.created_at)}</span>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap break-words">{it.message}</p>
                    <p className="text-xs text-muted-foreground mt-3">
                      {it.user_name ? (
                        <>De <span className="font-semibold text-foreground">{it.user_name}</span>{it.user_email ? ` · ${it.user_email}` : ""}</>
                      ) : (
                        "Usuario anónimo"
                      )}
                    </p>
                    {it.reply_message && (
                      <div className="mt-3 border-l-2 border-primary bg-primary/5 pl-3 py-2" data-testid="feedback-reply-sent">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-primary flex items-center gap-1.5 mb-1">
                          <MailCheck className="w-3.5 h-3.5" /> Respondido
                        </p>
                        <p className="text-sm text-foreground whitespace-pre-wrap break-words">{it.reply_message}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-4">
                      {resolved ? (
                        <Button
                          data-testid="feedback-reopen"
                          onClick={() => setStatus(it.id, "nuevo")}
                          variant="outline"
                          className="rounded-none border-border h-9 text-xs gap-1.5"
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Reabrir
                        </Button>
                      ) : (
                        <Button
                          data-testid="feedback-resolve"
                          onClick={() => setStatus(it.id, "resuelto")}
                          className="rounded-none bg-primary hover:bg-blue-900 h-9 text-xs gap-1.5"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Marcar resuelto
                        </Button>
                      )}
                      {it.user_email && (
                        <Button
                          data-testid="feedback-reply-toggle"
                          onClick={() => (replyingId === it.id ? setReplyingId(null) : openReply(it.id))}
                          variant="outline"
                          className="rounded-none border-border h-9 text-xs gap-1.5"
                        >
                          <Reply className="w-3.5 h-3.5" /> Responder
                        </Button>
                      )}
                      <Button
                        data-testid="feedback-delete"
                        onClick={() => remove(it.id)}
                        variant="outline"
                        className="rounded-none border-border h-9 w-9 px-0 text-muted-foreground hover:text-red-600 hover:border-red-300"
                        aria-label="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {replyingId === it.id && (
                      <div className="mt-4 border-t border-border pt-4" data-testid="feedback-reply-form">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                          Responder a {it.user_email}
                        </p>
                        <Textarea
                          data-testid="feedback-reply-input"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          maxLength={4000}
                          placeholder="Escribe tu respuesta… (se enviará por email al usuario)"
                          className="rounded-none resize-none h-24 text-sm"
                        />
                        <div className="flex items-center gap-2 mt-3">
                          <Button
                            data-testid="feedback-reply-send"
                            onClick={() => sendReply(it.id)}
                            disabled={replySending}
                            className="rounded-none bg-primary hover:bg-blue-900 h-9 text-xs gap-1.5"
                          >
                            {replySending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            {replySending ? "Enviando…" : "Enviar respuesta"}
                          </Button>
                          <Button
                            data-testid="feedback-reply-cancel"
                            onClick={() => setReplyingId(null)}
                            variant="outline"
                            className="rounded-none border-border h-9 text-xs"
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {preview && (
        <div
          data-testid="feedback-preview-overlay"
          onClick={() => setPreview(null)}
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6 animate-in fade-in duration-150"
        >
          <button
            className="absolute top-5 right-5 text-white/80 hover:text-white"
            onClick={() => setPreview(null)}
            aria-label="Cerrar"
          >
            <X className="w-7 h-7" />
          </button>
          <img src={preview} alt="Captura ampliada" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
